// Backup Manager Component
import React, { useState, useEffect } from 'react';
import { Download, Upload, RefreshCw, Database, FileJson, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BackupManager = () => {
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchBackupInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/info`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBackupInfo(data);
      }
    } catch (error) {
      console.error('Error fetching backup info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackupInfo();
  }, []);

  const handleDownloadBackup = async () => {
    setDownloading(true);
    setMessage(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameMatch = contentDisposition?.match(/filename=(.+)/);
        const filename = filenameMatch ? filenameMatch[1] : 'sae_backup.json';
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setMessage({ type: 'success', text: 'Backup téléchargé avec succès!' });
      } else {
        setMessage({ type: 'error', text: 'Erreur lors du téléchargement' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion' });
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!window.confirm('⚠️ ATTENTION: Cette action va remplacer toutes les données actuelles par celles du backup. Êtes-vous sûr de vouloir continuer?')) {
      return;
    }
    
    setRestoring(true);
    setMessage(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/restore`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ 
          type: 'success', 
          text: `Restauration réussie! ${Object.values(data.restored_collections).reduce((a, b) => a + b, 0)} documents restaurés.` 
        });
        fetchBackupInfo();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors de la restauration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion' });
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Database className="w-5 h-5" />
            Sauvegarde & Restauration
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Gérez les sauvegardes de votre base de données
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchBackupInfo}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {message.text}
        </div>
      )}

      {/* Backup Info */}
      {backupInfo && (
        <div className="bg-slate-50 rounded-lg p-6">
          <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
            <FileJson className="w-4 h-4" />
            Contenu de la sauvegarde
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {backupInfo.collections?.slice(0, 8).map((col) => (
              <div key={col.name} className="bg-white p-3 rounded-lg border border-slate-200">
                <div className="text-xs text-slate-500 capitalize">{col.name.replace('_', ' ')}</div>
                <div className="text-lg font-semibold text-slate-800">{col.document_count}</div>
              </div>
            ))}
          </div>
          
          <div className="text-sm text-slate-600 bg-amber-50 p-3 rounded-lg">
            <strong>📝 Note:</strong> {backupInfo.backup_includes}
          </div>
          
          <div className="mt-4 text-sm text-slate-500">
            Total: <strong>{backupInfo.total_documents}</strong> documents
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Download Backup */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Download className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-slate-800">Télécharger une sauvegarde</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                Créez une sauvegarde complète de toutes les données (textes, configurations, utilisateurs).
              </p>
              <Button 
                onClick={handleDownloadBackup}
                disabled={downloading}
                className="w-full"
              >
                {downloading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger le backup
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Restore Backup */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Upload className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-slate-800">Restaurer une sauvegarde</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                ⚠️ Cette action remplacera toutes les données actuelles par celles du fichier de backup.
              </p>
              <label className="block">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleRestore}
                  disabled={restoring}
                  className="hidden"
                />
                <Button 
                  variant="outline"
                  className="w-full cursor-pointer"
                  disabled={restoring}
                  asChild
                >
                  <span>
                    {restoring ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Restauration en cours...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Sélectionner un fichier .json
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <strong>💡 Conseils:</strong>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>Téléchargez une sauvegarde avant de faire des modifications importantes</li>
          <li>Conservez vos backups sur un stockage externe (Google Drive, Dropbox, etc.)</li>
          <li>Les images uploadées ne sont pas incluses dans la sauvegarde</li>
          <li>Format du fichier: JSON lisible et éditable</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupManager;
